/**
 * AccountInfoForm Component
 *
 * Collects user account information for broker/company registration:
 * - First name, Last name
 * - Email address
 * - Password (with confirmation)
 * - Phone number
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, User, Mail, Lock } from 'lucide-react';
import PhoneInput from '../base/PhoneInput';
import type { AccountInfo } from '../../hooks/useRegistrationWizard';
import { COUNTRIES } from '../../config/countries';

const CURRENT_YEAR = new Date().getFullYear();
// Selectable birth years (ages 18–100). The platform is for adults only (18+),
// so under-18 years are not offered — the earliest year makes the user exactly 18.
const BIRTH_YEARS = Array.from({ length: 83 }, (_, i) => CURRENT_YEAR - 18 - i);

interface AccountInfoFormProps {
  initialData: AccountInfo;
  onChange: (data: AccountInfo) => void;
  onValidationChange: (isValid: boolean) => void;
}

export default function AccountInfoForm({
  initialData,
  onChange,
  onValidationChange,
}: AccountInfoFormProps) {
  const { t, i18n } = useTranslation('registration');
  const isRTL = i18n.language === 'ar';

  const [formData, setFormData] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    phone: string;
    gender: string;
    nationality: string;
    birthYear: string;
    confirmPassword: string;
  }>({
    firstName: initialData.firstName || '',
    lastName: initialData.lastName || '',
    email: initialData.email || '',
    password: initialData.password || '',
    phone: initialData.phone || '',
    gender: initialData.gender || '',
    nationality: initialData.nationality || '',
    birthYear: initialData.birthYear ? String(initialData.birthYear) : '',
    confirmPassword: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Validation rules
  const errors = {
    firstName: !formData.firstName.trim() ? t('account.errors.firstNameRequired', 'First name is required') : '',
    lastName: !formData.lastName.trim() ? t('account.errors.lastNameRequired', 'Last name is required') : '',
    email: !formData.email.trim()
      ? t('account.errors.emailRequired', 'Email is required')
      : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
        ? t('account.errors.emailInvalid', 'Please enter a valid email')
        : '',
    password: !formData.password
      ? t('account.errors.passwordRequired', 'Password is required')
      : formData.password.length < 8
        ? t('account.errors.passwordLength', 'Password must be at least 8 characters')
        : '',
    confirmPassword: !formData.confirmPassword
      ? t('account.errors.confirmPasswordRequired', 'Please confirm your password')
      : formData.password !== formData.confirmPassword
        ? t('account.errors.passwordMismatch', 'Passwords do not match')
        : '',
    phone: !formData.phone.trim()
      ? t('account.errors.phoneRequired', 'Phone number is required')
      : !/^[0-9+\-\s()]{9,}$/.test(formData.phone)
        ? t('account.errors.phoneInvalid', 'Please enter a valid phone number')
        : '',
  };

  const isValid = Object.values(errors).every((e) => !e);

  // Notify parent of validation changes
  useEffect(() => {
    onValidationChange(isValid);
  }, [isValid, onValidationChange]);

  // Notify parent of data changes (excluding confirmPassword). Optional
  // demographics are sent as undefined when blank, and birthYear as a number.
  useEffect(() => {
    const { confirmPassword, gender, nationality, birthYear, ...rest } = formData;
    onChange({
      ...rest,
      gender: gender || undefined,
      nationality: nationality || undefined,
      birthYear: birthYear ? Number(birthYear) : undefined,
    });
  }, [formData, onChange]);

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const inputClass = (field: keyof typeof errors) => `
    w-full px-4 py-3 rounded-xl border transition-colors
    ${isRTL ? 'pr-12' : 'pl-12'}
    ${touched[field] && errors[field]
      ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
      : 'border-gray-200 dark:border-gray-700 focus:border-[#004D40] focus:ring-[#1B7566]/30'
    }
    bg-white dark:bg-gray-800 text-gray-900 dark:text-white
    focus:outline-none focus:ring-2
  `;

  const selectClass =
    'w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:border-[#004D40] focus:ring-[#1B7566]/30 transition-colors';

  return (
    <div className="max-w-lg mx-auto" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2">
          {t('account.title', 'Create Your Account')}
        </h2>
        <p className="text-gray-500">
          {t('account.subtitle', 'Enter your details to get started')}
        </p>
      </div>

      <div className="space-y-5">
        {/* Name Row */}
        <div className="grid grid-cols-2 gap-4">
          {/* First Name */}
          <div>
            <label htmlFor="account-firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {t('account.firstName', 'First Name')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-4' : 'left-4'}`} aria-hidden="true" />
              <input
                type="text"
                id="account-firstName"
                name="firstName"
                autoComplete="given-name"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                onBlur={() => handleBlur('firstName')}
                placeholder={t('account.firstNamePlaceholder', 'John')}
                className={inputClass('firstName')}
                aria-invalid={touched.firstName && !!errors.firstName}
                aria-describedby={touched.firstName && errors.firstName ? 'account-firstName-error' : undefined}
              />
            </div>
            {touched.firstName && errors.firstName && (
              <p id="account-firstName-error" className="mt-1 text-sm text-red-500" aria-live="polite">{errors.firstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="account-lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {t('account.lastName', 'Last Name')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-4' : 'left-4'}`} aria-hidden="true" />
              <input
                type="text"
                id="account-lastName"
                name="lastName"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                onBlur={() => handleBlur('lastName')}
                placeholder={t('account.lastNamePlaceholder', 'Doe')}
                className={inputClass('lastName')}
                aria-invalid={touched.lastName && !!errors.lastName}
                aria-describedby={touched.lastName && errors.lastName ? 'account-lastName-error' : undefined}
              />
            </div>
            {touched.lastName && errors.lastName && (
              <p id="account-lastName-error" className="mt-1 text-sm text-red-500" aria-live="polite">{errors.lastName}</p>
            )}
          </div>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="account-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {t('account.email', 'Email Address')} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Mail className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-4' : 'left-4'}`} aria-hidden="true" />
            <input
              type="email"
              id="account-email"
              name="email"
              autoComplete="email"
              spellCheck={false}
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              onBlur={() => handleBlur('email')}
              placeholder={t('account.emailPlaceholder', 'you@example.com')}
              className={inputClass('email')}
              aria-invalid={touched.email && !!errors.email}
              aria-describedby={touched.email && errors.email ? 'account-email-error' : undefined}
            />
          </div>
          {touched.email && errors.email && (
            <p id="account-email-error" className="mt-1 text-sm text-red-500" aria-live="polite">{errors.email}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="account-phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {t('account.phone', 'Phone Number')} <span className="text-red-500">*</span>
          </label>
          <PhoneInput
            id="account-phone"
            value={formData.phone}
            onChange={(value) => handleChange('phone', value)}
            onBlur={() => handleBlur('phone')}
            required
            hasError={touched.phone && !!errors.phone}
          />
          {touched.phone && errors.phone && (
            <p id="account-phone-error" className="mt-1 text-sm text-red-500" aria-live="polite">{errors.phone}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="account-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {t('account.password', 'Password')} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Lock className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-4' : 'left-4'}`} aria-hidden="true" />
            <input
              type={showPassword ? 'text' : 'password'}
              id="account-password"
              name="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              onBlur={() => handleBlur('password')}
              placeholder={t('account.passwordPlaceholder', 'At least 8 characters')}
              className={`${inputClass('password')} ${isRTL ? 'pl-12' : 'pr-12'}`}
              aria-invalid={touched.password && !!errors.password}
              aria-describedby={touched.password && errors.password ? 'account-password-error' : undefined}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? t('account.hidePassword', 'Hide password') : t('account.showPassword', 'Show password')}
              aria-pressed={showPassword}
              className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 ${isRTL ? 'left-4' : 'right-4'}`}
            >
              {showPassword ? <EyeOff className="w-5 h-5" aria-hidden="true" /> : <Eye className="w-5 h-5" aria-hidden="true" />}
            </button>
          </div>
          {touched.password && errors.password && (
            <p id="account-password-error" className="mt-1 text-sm text-red-500" aria-live="polite">{errors.password}</p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="account-confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {t('account.confirmPassword', 'Confirm Password')} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Lock className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-4' : 'left-4'}`} aria-hidden="true" />
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="account-confirmPassword"
              name="confirmPassword"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={(e) => handleChange('confirmPassword', e.target.value)}
              onBlur={() => handleBlur('confirmPassword')}
              placeholder={t('account.confirmPasswordPlaceholder', 'Re-enter your password')}
              className={`${inputClass('confirmPassword')} ${isRTL ? 'pl-12' : 'pr-12'}`}
              aria-invalid={touched.confirmPassword && !!errors.confirmPassword}
              aria-describedby={touched.confirmPassword && errors.confirmPassword ? 'account-confirmPassword-error' : undefined}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? t('account.hidePassword', 'Hide password') : t('account.showPassword', 'Show password')}
              aria-pressed={showConfirmPassword}
              className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 ${isRTL ? 'left-4' : 'right-4'}`}
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" aria-hidden="true" /> : <Eye className="w-5 h-5" aria-hidden="true" />}
            </button>
          </div>
          {touched.confirmPassword && errors.confirmPassword && (
            <p id="account-confirmPassword-error" className="mt-1 text-sm text-red-500" aria-live="polite">{errors.confirmPassword}</p>
          )}
        </div>

        {/* Optional self-reported demographics (Statistics platform) */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label htmlFor="account-gender" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {t('account.gender', 'Gender')}
            </label>
            <select
              id="account-gender"
              name="gender"
              value={formData.gender}
              onChange={(e) => handleChange('gender', e.target.value)}
              className={selectClass}
            >
              <option value="">{t('account.genderSelect', 'Select (optional)')}</option>
              <option value="MALE">{t('account.genderMale', 'Male')}</option>
              <option value="FEMALE">{t('account.genderFemale', 'Female')}</option>            </select>
          </div>
          <div>
            <label htmlFor="account-nationality" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {t('account.nationality', 'Nationality')}
            </label>
            <select
              id="account-nationality"
              name="nationality"
              value={formData.nationality}
              onChange={(e) => handleChange('nationality', e.target.value)}
              className={selectClass}
            >
              <option value="">{t('account.nationalitySelect', 'Select (optional)')}</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{isRTL ? c.ar : c.en}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="account-birthYear" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {t('account.birthYear', 'Year of Birth')}
            </label>
            <select
              id="account-birthYear"
              name="birthYear"
              value={formData.birthYear}
              onChange={(e) => handleChange('birthYear', e.target.value)}
              className={selectClass}
            >
              <option value="">{t('account.birthYearSelect', 'Select (optional)')}</option>
              {BIRTH_YEARS.map((y) => (
                <option key={y} value={String(y)}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-xs text-gray-400">
          {t('account.demographicsNote', 'Optional - helps us understand our audience. You can change or remove this anytime in your account settings.')}
        </p>
      </div>
    </div>
  );
}
